import React, { useState, useRef } from 'react';
import { Slide, SlideFormData, SlideImage, PriceRecord, QuickAnalyzeResponse, RateLimitError, hasAnalyzableImage } from '../types';
import { slideApiService } from '../services/slideApiService';
import CategoryEditor from './CategoryEditor';
import LocationPlaceEditor from './LocationPlaceEditor';
import ImageManager from './ImageManager';
import PriceHistoryManager, { PriceHistoryManagerHandle } from './PriceHistoryManager';
import QuickAnalyzeResultsOverlay from './QuickAnalyzeResultsOverlay';
import QuickAnalyzeInputModal from './QuickAnalyzeInputModal';
import RateLimitModal from './RateLimitModal';

interface SlideEditModalProps {
  slide: Slide;
  onClose: () => void;
  onSlideUpdated: (updatedSlide: Slide) => void;
}

const SlideEditModal: React.FC<SlideEditModalProps> = ({
  slide,
  onClose,
  onSlideUpdated
}) => {
  // Debug logging
  console.log('=== SlideEditModal - Received slide data ===');
  console.log('Slide ID:', slide.id);
  console.log('Location/Place data:', {
    locationId: slide.locationId,
    placeId: slide.placeId,
    locationName: slide.locationName,
    placeName: slide.placeName
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rotatingImages, setRotatingImages] = useState(false);
  const [croppingImages, setCroppingImages] = useState(false);

  // Form data state
  const [name, setName] = useState(slide.name || '');
  const [description, setDescription] = useState(slide.description || slide.desc || '');
  // v1.1.5 Internal Notes + Marketing Description feature (internal edit fields).
  const [marketingDescription, setMarketingDescription] = useState(slide.marketingDescription || '');
  const [marketplaceTitle, setMarketplaceTitle] = useState(slide.marketplaceTitle || '');
  // Tags are edited as a raw comma-separated string (Etsy's format) and parsed to an array on save.
  const [marketplaceTagsText, setMarketplaceTagsText] = useState((slide.marketplaceTags || []).join(', '));
  const [notes, setNotes] = useState(slide.notes || '');
  const [categories, setCategories] = useState<string[]>(slide.categories || []);
  const [locationId, setLocationId] = useState<string | undefined>(slide.locationId);
  const [placeId, setPlaceId] = useState<string | undefined>(slide.placeId);
  const [images, setImages] = useState<SlideImage[]>(slide.images || []);
  const [priceHistory, setPriceHistory] = useState<PriceRecord[]>(slide.priceHistory || []);
  const [isActive, setIsActive] = useState(slide.active === true);
  // Phase 3b: publish state is pending-until-Save (mirrors iOS). `slidePublished` is the
  // slide-level flag; `publishedImageIds` is the pending set of published image IDs. The
  // green bands + toggle-enabled state update live from these; nothing hits the server
  // until Save (updateSlide PUT, then one authoritative setSlidePublished PATCH).
  const [slidePublished, setSlidePublished] = useState(slide.published === true);
  const [publishedImageIds, setPublishedImageIds] = useState<Set<string>>(
    new Set((slide.images || []).filter(img => img.published).map(img => img.id))
  );
  const [imageRotations, setImageRotations] = useState<{[imageId: string]: number}>({});
  const [imageCrops, setImageCrops] = useState<{[imageId: string]: { x: number; y: number; width: number; height: number }}>({});

  // Quick Analyze state
  const [showQuickAnalyzeOverlay, setShowQuickAnalyzeOverlay] = useState(false);
  const [showQuickAnalyzeInput, setShowQuickAnalyzeInput] = useState(false);
  const [quickAnalyzeText, setQuickAnalyzeText] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<QuickAnalyzeResponse | null>(null);
  const [analysisError, setAnalysisError] = useState('');
  const [rateLimitError, setRateLimitError] = useState<RateLimitError | null>(null);

  // Permission state. Two separate rules: `active` is shop-internal (MEMBER may change it);
  // publishing controls what clients can see (PLATFORM_ADMIN/OWNER/ADMIN only).
  const [canModifyActive, setCanModifyActive] = useState(false);
  const [canPublish, setCanPublish] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  // Ref to access PriceHistoryManager imperative methods
  const priceHistoryRef = useRef<PriceHistoryManagerHandle>(null);

  // Non-blocking "overlay fresh slide" support: flips true on the first user interaction so a
  // late-arriving refresh never clobbers in-progress edits.
  const interactedRef = useRef(false);

  // Check if user can modify active property
  React.useEffect(() => {
    const checkActivePermission = async () => {
      try {
        setCheckingPermissions(true);
        const result = await slideApiService.canModifyActiveProperty(slide.shopId, slide.id);
        setCanModifyActive(result.canModifyActive);
        // A server that predates the active/publish split has no canPublish: fall back to the old
        // single rule, under which canModifyActive meant management roles.
        setCanPublish(result.canPublish ?? result.canModifyActive);
      } catch (err) {
        console.error('Failed to check active property permission:', err);
        // Default to false if permission check fails
        setCanModifyActive(false);
        setCanPublish(false);
      } finally {
        setCheckingPermissions(false);
      }
    };

    checkActivePermission();
  }, [slide.id]);

  // Update images when slide prop changes (e.g., after rotation and reopening modal)
  React.useEffect(() => {
    setImages(slide.images || []);
  }, [slide.images, slide.id]);

  // Phase 3b: reseed pending publish state only when a genuinely different slide loads
  // (keyed on slide.id, NOT slide.images) so a live edit isn't clobbered by a prop refresh.
  React.useEffect(() => {
    setSlidePublished(slide.published === true);
    setPublishedImageIds(new Set((slide.images || []).filter(img => img.published).map(img => img.id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slide.id]);

  // Phase 3b: invariant — a published slide must have >=1 published image. If the last
  // published image is unpublished (or deleted/cropped away), auto-unpublish the slide.
  // This also disables/greys the toggle (disabled when the set is empty), mirroring iOS.
  React.useEffect(() => {
    if (slidePublished && publishedImageIds.size === 0) {
      setSlidePublished(false);
    }
  }, [publishedImageIds, slidePublished]);

  // Phase 3b: per-image publish toggle (from the ImageManager click-badge). Pending only.
  const handleTogglePublished = (imageId: string) => {
    setPublishedImageIds(prev => {
      const next = new Set(prev);
      if (next.has(imageId)) {
        next.delete(imageId);
      } else {
        next.add(imageId);
      }
      return next;
    });
  };

  // Phase 3b: surgical reconcile on any image change (add/delete/reorder). Keep published
  // IDs that still exist, drop only IDs that vanished. Reorder preserves IDs (selection
  // survives); a delete of a published photo drops just that ID. (Crop/rotate mint new IDs
  // at Save time and are reconciled there against the post-transform image list.)
  const handleImagesUpdate = (updatedImages: SlideImage[]) => {
    setImages(updatedImages);
    const currentIds = new Set(updatedImages.map(img => img.id));
    setPublishedImageIds(prev => {
      const filtered = new Set(Array.from(prev).filter(id => currentIds.has(id)));
      return filtered.size === prev.size ? prev : filtered;
    });
  };

  // Re-seed all editable state from a slide document. Used by the non-blocking refresh below.
  const seedFromSlide = (s: Slide) => {
    setName(s.name || '');
    setDescription(s.description || s.desc || '');
    setMarketingDescription(s.marketingDescription || '');
    setMarketplaceTitle(s.marketplaceTitle || '');
    setMarketplaceTagsText((s.marketplaceTags || []).join(', '));
    setNotes(s.notes || '');
    setCategories(s.categories || []);
    setLocationId(s.locationId);
    setPlaceId(s.placeId);
    setImages(s.images || []);
    setIsActive(s.active === true);
    setSlidePublished(s.published === true);
    setPublishedImageIds(new Set((s.images || []).filter(img => img.published).map(img => img.id)));
    // NOTE: price history is intentionally NOT overlaid here - PriceHistoryManager is seeded from
    // the `slide` prop and owns its own state, so it isn't part of this refresh.
  };

  // Non-blocking refresh-on-open (mirrors the iOS SlideDetailView refresh from P4). The modal
  // already renders immediately from the passed-in `slide` (state seeded above); here we quietly
  // fetch the authoritative slide by ID and overlay it, fixing the case where the list/search
  // results handed us a STALE slide (e.g. publish/active/fields changed elsewhere). Because it
  // lives in the edit modal, it covers every entry point (list/search/location/manage). Skipped
  // if the user has already started editing (interactedRef); on failure we keep the passed slide.
  React.useEffect(() => {
    let cancelled = false;
    slideApiService.getSlideById(slide.shopId, slide.id)
      .then(fresh => {
        if (cancelled || interactedRef.current) return;
        seedFromSlide(fresh);
      })
      .catch(err => {
        console.warn('Non-blocking slide refresh failed; keeping passed-in slide:', err);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slide.id, slide.shopId]);

  const handleRunQuickAnalyze = async (userText?: string) => {
    try {
      setShowQuickAnalyzeInput(false);
      setIsAnalyzing(true);
      setAnalysisError('');
      setShowQuickAnalyzeOverlay(true);
      setAnalysisResult(null);
      setQuickAnalyzeText(userText || '');

      console.log('Triggering Quick Analyze for existing slide:', slide.id);

      // Trigger analysis with optional user text
      const triggerResponse = await slideApiService.analyzeExistingSlide(slide.shopId, slide.id, userText || undefined);
      console.log('Quick Analyze triggered:', triggerResponse);

      if (triggerResponse.status !== 'processing' && triggerResponse.status !== 'success') {
        throw new Error(triggerResponse.message || 'Failed to trigger analysis');
      }

      const analysisId = triggerResponse.analysis_id; // Backend uses snake_case

      // Poll for results (2s intervals, 30s max timeout)
      const maxAttempts = 15; // 15 attempts * 2s = 30s
      let attempts = 0;

      const pollForResults = async (): Promise<void> => {
        attempts++;
        console.log(`Polling for results (attempt ${attempts}/${maxAttempts})...`);

        try {
          const result = await slideApiService.getQuickAnalyzeResults(slide.shopId, analysisId);

          // QuickAnalyzeResponse doesn't have a status field - if we get a result, it's successful
          console.log('Quick Analyze results received:', result);
          setAnalysisResult(result);
          setIsAnalyzing(false);
          return;

          // Still processing - continue polling if we haven't hit max attempts
          if (attempts < maxAttempts) {
            setTimeout(pollForResults, 2000);
          } else {
            throw new Error('Analysis timeout - please try again');
          }
        } catch (pollError) {
          console.error('Error polling for results:', pollError);
          if (attempts < maxAttempts) {
            setTimeout(pollForResults, 2000);
          } else {
            throw new Error('Failed to retrieve analysis results');
          }
        }
      };

      // Start polling
      setTimeout(pollForResults, 2000);

    } catch (error) {
      console.error('Error running Quick Analyze:', error);
      if (error instanceof RateLimitError) {
        setRateLimitError(error);
        setAnalysisError('');
        setShowQuickAnalyzeOverlay(false);
      } else {
        setAnalysisError(error instanceof Error ? error.message : 'Failed to analyze slide');
      }
      setIsAnalyzing(false);
    }
  };

  const handleApplyAnalysis = () => {
    if (!analysisResult) return;

    console.log('Applying Quick Analyze results to slide...');

    // Update name if AI suggested a title
    if (analysisResult.suggestedTitle) {
      setName(analysisResult.suggestedTitle);
    }

    // Build description from AI analysis, matching the backend AnalysisApplicationService
    // (includeIdentification=true): Era/Maker/Style first, then Features/Materials/Condition/Collectibility.
    const descriptionParts: string[] = [];

    if (analysisResult.suggestedEra) {
      const era = analysisResult.suggestedEra.trim();
      descriptionParts.push(analysisResult.stylePeriod
        ? `Era: ${era} (${analysisResult.stylePeriod.trim()})`
        : `Era: ${era}`);
    } else if (analysisResult.stylePeriod) {
      descriptionParts.push(`Style Period: ${analysisResult.stylePeriod.trim()}`);
    }

    if (analysisResult.suggestedMaker) {
      descriptionParts.push(`Maker: ${analysisResult.suggestedMaker.trim()}`);
    }

    if (analysisResult.distinguishingFeatures) {
      descriptionParts.push(`Features: ${analysisResult.distinguishingFeatures.trim()}`);
    }

    if (analysisResult.materials && analysisResult.materials.length > 0) {
      descriptionParts.push(`Materials: ${analysisResult.materials.join(', ')}`);
    }

    if (analysisResult.conditionNotes) {
      descriptionParts.push(`Condition: ${analysisResult.conditionNotes.trim()}`);
    }

    if (analysisResult.collectibilityNotes) {
      descriptionParts.push(`Collectibility: ${analysisResult.collectibilityNotes.trim()}`);
    }

    if (descriptionParts.length > 0) {
      setDescription(descriptionParts.join('\n\n'));
    }

    // v1.1.5: apply the AI marketing outputs too (parallels the backend convert-to-slide / auto-QA
    // apply, which copy marketing onto the slide). The marketing TITLE maps to Marketplace Title.
    if (analysisResult.marketingDescription) {
      setMarketingDescription(analysisResult.marketingDescription.trim());
    }
    if (analysisResult.marketingTitle) {
      setMarketplaceTitle(analysisResult.marketingTitle.trim());
    }
    if (analysisResult.marketplaceTags && analysisResult.marketplaceTags.length > 0) {
      setMarketplaceTagsText(analysisResult.marketplaceTags.join(', '));
    }

    // Merge AI-suggested categories with existing categories (deduplicate)
    // Prefer categoryTags (search-optimized) when available
    const aiCategories: string[] = [];

    if (analysisResult.categoryTags && analysisResult.categoryTags.length > 0) {
      analysisResult.categoryTags.forEach(tag => {
        const t = tag.toUpperCase();
        if (t.length <= 50) {
          aiCategories.push(t);
        }
      });
    } else {
      // Fallback for pre-categoryTags analyses
      if (analysisResult.suggestedEra) {
        const era = analysisResult.suggestedEra.toUpperCase();
        if (era.length <= 50) {
          aiCategories.push(era);
        }
      }

      if (analysisResult.stylePeriod) {
        const style = analysisResult.stylePeriod.toUpperCase();
        if (style.length <= 50) {
          aiCategories.push(style);
        }
      }

      if (analysisResult.marketplaceKeywords) {
        analysisResult.marketplaceKeywords.forEach(keyword => {
          const kw = keyword.toUpperCase();
          if (kw.length <= 50) {
            aiCategories.push(kw);
          }
        });
      }
    }

    // Deduplicate and merge with existing categories, capped at the 50-category limit
    // (slide-svc rejects >50). Existing categories come first so they're preserved; new
    // AI categories fill the remaining slots. Re-running QA is now common (marketing desc),
    // and the merge is additive, so without this cap the save 400s once past 50.
    const mergedCategories = Array.from(new Set([...categories, ...aiCategories])).slice(0, 50);
    setCategories(mergedCategories);

    // Add ACQUISITION price record if price tag detected and no ACQUISITION record exists
    if (analysisResult.priceTagDetected && analysisResult.detectedPrice) {
      // Check if an ACQUISITION record already exists
      const hasAcquisitionRecord = priceHistory.some(record => record.type === 'ACQUISITION');

      if (!hasAcquisitionRecord) {
        const now = new Date().toISOString(); // Full ISO-8601 with timezone

        const newPriceRecord: PriceRecord = {
          id: crypto.randomUUID(),
          type: 'ACQUISITION',
          amount: analysisResult.detectedPrice,
          currency: analysisResult.detectedCurrency || 'USD',
          recordedAt: now,
          effectiveDate: now, // Use full ISO-8601 format, not just date
          notes: analysisResult.detectedRetailer
            ? `Acquired from ${analysisResult.detectedRetailer}`
            : 'Acquired (detected from price tag)',
        };

        setPriceHistory([...priceHistory, newPriceRecord]);

        // Also update the PriceHistoryManager to show the new record
        if (priceHistoryRef.current) {
          // The manager will pick up the new record when we call setPriceHistory
        }
      } else {
        console.log('ACQUISITION record already exists, skipping duplicate');
      }
    }

    console.log('Quick Analyze results applied successfully');

    // Close overlay
    setShowQuickAnalyzeOverlay(false);
    setAnalysisResult(null);
    setIsAnalyzing(false);
  };

  const handleCancelAnalysis = () => {
    setShowQuickAnalyzeOverlay(false);
    setAnalysisResult(null);
    setIsAnalyzing(false);
    setAnalysisError('');
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      // Step 0: Commit any pending price history changes before saving
      let finalPriceHistory = priceHistory;
      if (priceHistoryRef.current) {
        const committedPriceHistory = priceHistoryRef.current.commitPendingChanges();
        if (committedPriceHistory) {
          console.log('Auto-committed pending price history changes');
          finalPriceHistory = committedPriceHistory;
        }
      }

      // Step 1: Apply image rotations if any
      // Track updated images so updateSlide sends current IDs (not stale pre-rotation IDs)
      let currentImages = [...images];
      const rotationsToApply = Object.entries(imageRotations).filter(([_, degrees]) => degrees !== 0);

      if (rotationsToApply.length > 0) {
        setRotatingImages(true);
        console.log(`Applying rotations to ${rotationsToApply.length} image(s)...`);

        for (const [imageId, degrees] of rotationsToApply) {
          try {
            const newImage = await slideApiService.rotateImage(slide.shopId, slide.id, imageId, degrees);
            console.log(`Rotated image ${imageId} -> ${newImage.id} by ${degrees}°`);
            // Update local images with new ID so updateSlide doesn't overwrite the swap
            const idx = currentImages.findIndex(img => img.id === imageId);
            if (idx !== -1) {
              currentImages[idx] = { ...currentImages[idx], id: newImage.id, filename: newImage.filename };
            }
          } catch (rotError) {
            console.error(`Failed to rotate image ${imageId}:`, rotError);
            // Continue with other rotations even if one fails
          }
        }

        setRotatingImages(false);
      }

      // Step 1.5: Apply image crops if any
      const cropsToApply = Object.entries(imageCrops);

      if (cropsToApply.length > 0) {
        setCroppingImages(true);
        console.log(`Applying crops to ${cropsToApply.length} image(s)...`);

        for (const [imageId, cropArea] of cropsToApply) {
          try {
            const newImage = await slideApiService.cropImage(slide.shopId, slide.id, imageId, cropArea);
            console.log(`Cropped image ${imageId} -> ${newImage.id}`);
            // Update local images with new ID so updateSlide doesn't overwrite the swap
            const idx = currentImages.findIndex(img => img.id === imageId);
            if (idx !== -1) {
              currentImages[idx] = { ...currentImages[idx], id: newImage.id, filename: newImage.filename };
            }
          } catch (cropError) {
            console.error(`Failed to crop image ${imageId}:`, cropError);
            // Continue with other crops even if one fails
          }
        }

        setCroppingImages(false);
      }

      // Step 2: Update slide data (using currentImages which has post-rotation/crop IDs)
      console.log('Submitting slide with price history:', finalPriceHistory);

      const slideData: SlideFormData = {
        ...(name.trim() && { name: name.trim() }),
        ...(description.trim() && { description: description.trim() }),
        // v1.1.5 marketing/notes fields. updateSlide (PUT) is a FULL REPLACE server-side, so these
        // MUST be sent to preserve them — omitting a populated field would null it out. Empty text
        // fields are intentionally omitted (equivalent to clearing); tags always send the array.
        ...(marketingDescription.trim() && { marketingDescription: marketingDescription.trim() }),
        ...(marketplaceTitle.trim() && { marketplaceTitle: marketplaceTitle.trim() }),
        marketplaceTags: marketplaceTagsText.split(',').map(t => t.trim()).filter(Boolean),
        ...(notes.trim() && { notes: notes.trim() }),
        categories: categories,
        shopId: slide.shopId,
        images: currentImages,
        priceHistory: finalPriceHistory,
        active: isActive,
        locationId: locationId,
        placeId: placeId
      };

      console.log('Slide data being sent:', slideData);

      const updatedSlide = await slideApiService.updateSlide(slide.shopId, slide.id, slideData);

      // Step 3: Phase 3b authoritative publish write. Reconcile the pending published-image
      // set against the POST-rotation/crop image IDs (crop/rotate mint NEW ids, so a photo
      // that was cropped/rotated correctly drops out of the published set), then full-replace
      // the per-image flags + set the slide flag on the server. `updateSlide` (PUT) above
      // preserves published server-side; this PATCH is the authoritative publish state.
      //
      // Only for users who may publish. The PATCH is management-only on the server, so sending it
      // for a MEMBER would fail the whole save with a 403 even though the PUT above succeeded.
      // Without it, a MEMBER's save leaves the publish state as it was (the server keeps the
      // "published slide has >=1 published image" invariant itself).
      const currentImageIds = new Set(currentImages.map(img => img.id));
      const finalPublishedImageIds = Array.from(publishedImageIds).filter(id => currentImageIds.has(id));
      // Enforce the invariant client-side too: no published images => slide can't be published.
      const finalSlidePublished = finalPublishedImageIds.length > 0 ? slidePublished : false;
      if (canPublish) {
        await slideApiService.setSlidePublished(
          slide.shopId,
          slide.id,
          finalSlidePublished,
          finalPublishedImageIds
        );
      }

      // If we rotated or cropped any images, fetch fresh slide data to ensure
      // the UI has the complete server-side image state (getSlideById returns published).
      let finalSlide: Slide;
      if (rotationsToApply.length > 0 || cropsToApply.length > 0) {
        console.log('Fetching fresh slide data after image transformations...');
        finalSlide = await slideApiService.getSlideById(slide.shopId, slide.id);
      } else {
        const publishedIdSet = new Set(finalPublishedImageIds);
        finalSlide = {
          ...updatedSlide,
          published: finalSlidePublished,
          images: currentImages.map(img => ({ ...img, published: publishedIdSet.has(img.id) })),
          priceHistory: finalPriceHistory
        };
      }

      onSlideUpdated(finalSlide);
      onClose();
    } catch (error) {
      console.error('Error updating slide:', error);
      setError('Failed to update slide. Please try again.');
    } finally {
      setLoading(false);
      setRotatingImages(false);
      setCroppingImages(false);
    }
  };

  return (
    <div
      className="modal-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1050,
        padding: '8px'
      }}
      onClick={onClose}
    >
      <div
        className="modal-content"
        style={{
          backgroundColor: 'var(--bg-card)',
          borderRadius: '8px',
          maxWidth: '1200px',
          width: '100%',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
        // Any user interaction (typing, toggling, clicking a control) marks the form dirty so the
        // non-blocking refresh above won't overlay fresh data on top of in-progress edits.
        onInputCapture={() => { interactedRef.current = true; }}
        onChangeCapture={() => { interactedRef.current = true; }}
        onClickCapture={() => { interactedRef.current = true; }}
      >
        {/* Modal Header */}
        <div style={{
          padding: '16px',
          borderBottom: '1px solid var(--border-light)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0
        }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>Edit Slide</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: 'var(--text-primary)'
            }}
          >
            ×
          </button>
        </div>

        {/* Modal Body - Scrollable */}
        <div style={{ padding: '16px', overflow: 'auto', flex: 1 }}>
          {error && (
            <div style={{
              color: 'var(--error-text)',
              backgroundColor: 'var(--error-bg)',
              border: '1px solid var(--error-border)',
              borderRadius: '4px',
              padding: '12px',
              marginBottom: '20px'
            }}>
              {error}
            </div>
          )}

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '20px'
          }}>
            {/* Left Column */}
            <div>
              {/* Basic Information */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ marginBottom: '16px' }}>Slide Information</h3>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                    Name
                  </label>
                  <textarea
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Optional"
                    rows={2}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--border-light)',
                      borderRadius: '4px',
                      fontSize: '16px',
                      resize: 'vertical',
                      fontFamily: 'inherit',
                      lineHeight: '1.5'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                    Description
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--border-light)',
                      borderRadius: '4px',
                      fontSize: '16px',
                      resize: 'vertical'
                    }}
                  />
                </div>

                {/* v1.1.5 Marketing Description — customer-facing copy shown to CLIENT/VENDOR in
                    place of the internal description. */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                    Marketing Description
                  </label>
                  <textarea
                    value={marketingDescription}
                    onChange={(e) => setMarketingDescription(e.target.value)}
                    rows={4}
                    placeholder="Customer-facing copy shown to clients and patrons. Auto-generated by Quick Analyze; edit before sharing."
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--border-light)',
                      borderRadius: '4px',
                      fontSize: '16px',
                      resize: 'vertical'
                    }}
                  />
                </div>

                {/* v1.1.5 Marketplace Title — internal Etsy-listing helper (copy/paste out). */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                    Marketplace Title
                  </label>
                  <textarea
                    value={marketplaceTitle}
                    onChange={(e) => setMarketplaceTitle(e.target.value)}
                    rows={2}
                    placeholder="Suggested marketplace/Etsy listing title."
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--border-light)',
                      borderRadius: '4px',
                      fontSize: '16px',
                      resize: 'vertical'
                    }}
                  />
                </div>

                {/* v1.1.5 Marketplace Tags — internal Etsy-listing helper, comma-separated (<= 13). */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                    Marketplace Tags
                  </label>
                  <textarea
                    value={marketplaceTagsText}
                    onChange={(e) => setMarketplaceTagsText(e.target.value)}
                    rows={2}
                    placeholder="Comma-separated tags for Etsy/marketplace listings (up to 13)."
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--border-light)',
                      borderRadius: '4px',
                      fontSize: '16px',
                      resize: 'vertical'
                    }}
                  />
                </div>

                {/* v1.1.5 Notes — internal-only shop notes, never shown to external roles. */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                    Notes (internal only)
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    placeholder="Private notes for your shop. Never shown to clients or patrons."
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--border-light)',
                      borderRadius: '4px',
                      fontSize: '16px',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </div>

              {/* Categories */}
              <div style={{ marginBottom: '24px' }}>
                <CategoryEditor
                  categories={categories}
                  onCategoriesChange={setCategories}
                  shopId={slide.shopId}
                />
              </div>

              {/* Location / Place */}
              <div style={{ marginBottom: '24px' }}>
                <LocationPlaceEditor
                  locationId={locationId}
                  placeId={placeId}
                  onLocationChange={setLocationId}
                  onPlaceChange={setPlaceId}
                  shopId={slide.shopId}
                />
              </div>

              {/* Price History */}
              <div>
                <PriceHistoryManager
                  ref={priceHistoryRef}
                  slideId={slide.id}
                  initialPriceHistory={slide.priceHistory || []}
                  onPriceHistoryUpdate={setPriceHistory}
                />
              </div>
            </div>

            {/* Right Column */}
            <div>
              <ImageManager
                shopId={slide.shopId}
                slideId={slide.id}
                images={images}
                onImagesUpdate={handleImagesUpdate}
                onRotationsUpdate={setImageRotations}
                onCropsUpdate={setImageCrops}
                publishedImageIds={canPublish ? publishedImageIds : undefined}
                onTogglePublished={canPublish ? handleTogglePublished : undefined}
              />
            </div>
          </div>
        </div>

        {/* Modal Footer - Fixed at bottom */}
        <div style={{
          padding: '16px',
          borderTop: '1px solid var(--border-light)',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          flexShrink: 0,
          backgroundColor: 'var(--bg-card)'
        }}>
          {/* Active Property - PLATFORM_ADMIN, OWNER, ADMIN and MEMBER (shop-internal flag) */}
          {!checkingPermissions && canModifyActive && (
            <div style={{ width: '100%' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  style={{ marginRight: '8px' }}
                />
                <span style={{ fontSize: '14px', fontWeight: 500 }}>Slide is active</span>
              </label>
            </div>
          )}

          {/* Phase 3b: Published toggle - PLATFORM_ADMIN/OWNER/ADMIN only (canPublish). Disabled + dimmed until
              the slide has >=1 published image (click a photo's badge to publish it). Static
              helper text (always shown) so the layout never reflows. Fixed "Published" label. */}
          {!checkingPermissions && canPublish && (
            <div style={{ width: '100%', opacity: publishedImageIds.size === 0 ? 0.4 : 1 }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: publishedImageIds.size === 0 ? 'not-allowed' : 'pointer' }}>
                <input
                  type="checkbox"
                  checked={slidePublished}
                  disabled={publishedImageIds.size === 0}
                  onChange={(e) => setSlidePublished(e.target.checked)}
                  style={{ marginRight: '8px' }}
                />
                <span style={{ fontSize: '14px', fontWeight: 500 }}>Published</span>
              </label>
              <div style={{ fontSize: '12px', color: 'var(--text-primary)', marginTop: '4px', marginLeft: '24px' }}>
                Publishing shares this slide and its published photos with external viewers. Click a photo's badge to publish it - at least one published photo is required.
              </div>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
            {/* Quick Analyze Button */}
            <button
              onClick={() => {
                setQuickAnalyzeText(name || '');
                setShowQuickAnalyzeInput(true);
              }}
              disabled={loading || isAnalyzing || !hasAnalyzableImage(images)}
              style={{
                width: '100%',
                padding: '10px 16px',
                border: '1px solid var(--primary-light)',
                backgroundColor: 'transparent',
                color: 'var(--primary-light)',
                borderRadius: '4px',
                cursor: (loading || isAnalyzing || !hasAnalyzableImage(images)) ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: 500,
                opacity: (loading || isAnalyzing || !hasAnalyzableImage(images)) ? 0.5 : 1
              }}
            >
              {isAnalyzing
                ? 'Analyzing...'
                : hasAnalyzableImage(images) ? 'Run Quick Analyze' : 'Add a photo to run Quick Analyze'}
            </button>

            {/* Standard Buttons */}
            <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
              <button
                onClick={onClose}
                disabled={loading}
                style={{
                  flex: '1',
                  padding: '10px 16px',
                  border: '1px solid var(--border-medium)',
                  backgroundColor: 'transparent',
                  color: 'var(--text-primary)',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                style={{
                  flex: '1',
                  padding: '10px 16px',
                  border: 'none',
                  backgroundColor: loading ? 'var(--disabled-bg)' : 'var(--primary)',
                  color: loading ? 'var(--disabled)' : 'var(--text-inverse)',
                  borderRadius: '4px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 500
                }}
              >
                {croppingImages ? 'Cropping Images...' : rotatingImages ? 'Rotating Images...' : loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Analyze Input Modal */}
      {showQuickAnalyzeInput && (
        <QuickAnalyzeInputModal
          shopId={slide.shopId}
          slideId={slide.id}
          suggestedText={quickAnalyzeText}
          slideImages={images || []}
          onAnalyze={handleRunQuickAnalyze}
          onClose={() => setShowQuickAnalyzeInput(false)}
        />
      )}

      {/* Quick Analyze Results Overlay */}
      {showQuickAnalyzeOverlay && (
        <QuickAnalyzeResultsOverlay
          analysisResult={analysisResult}
          isLoading={isAnalyzing}
          onApply={handleApplyAnalysis}
          onCancel={handleCancelAnalysis}
        />
      )}

      {/* Rate Limit Modal */}
      {rateLimitError && (
        <RateLimitModal
          error={rateLimitError}
          onClose={() => setRateLimitError(null)}
        />
      )}
    </div>
  );
};

export default SlideEditModal;